import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, ChevronDown, ChevronsUpDown, Globe, LogOut, Moon, Pencil, Sun, SunMoon, Trash2, User as UserIcon,
} from 'lucide-react';
import { signOutAndCloseSession } from '@core/session/signOut';
import { closeSession } from '@core/firebase/sessions';
import { signOut } from '@core/firebase/auth';
import { deleteMyAccount } from '@core/firebase/voiceNotes';
import { useManagedBy } from '@core/firebase/children';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, type Language } from '@/contexts/LanguageContext';
import { useTheme, type ThemePreference } from '@/contexts/ThemeContext';
import { ProfileSwitcher } from '@/components/ProfileSwitcher';
import { cn } from '@/lib/cn';

const LANGUAGES: { code: Language; nativeName: string }[] = [
  { code: 'ar', nativeName: 'العربية' },
  { code: 'en', nativeName: 'English' },
];

/** فاتح · داكن · تلقائي — the phone menu's appearance choice, in its order. */
const THEMES: { value: ThemePreference; labelKey: string; icon: typeof Sun }[] = [
  { value: 'light', labelKey: 'menu.light', icon: Sun },
  { value: 'dark', labelKey: 'menu.dark', icon: Moon },
  { value: 'system', labelKey: 'menu.system', icon: SunMoon },
];

/**
 * The person, at the foot of the sidebar, and the menu that opens from them.
 *
 * It bundles who you are, the appearance, the language and signing out into
 * ONE control, so the chrome does not sprout siblings — the sidebar holds the
 * sections and this; the top bar holds the role switch and the quick theme
 * toggle.
 *
 * The avatar is the first letter of the name rather than a monogram: every
 * name here is Arabic, and a two-letter latin initialism would be noise.
 */
export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { identity, isMuqri, isAdmin } = useAuth();
  // A managed child may not delete itself: the guardian consented for it, and
  // the server refuses. Everyone else must be able to — App Review 5.1.1(v).
  const { managedBy } = useManagedBy(identity?.userId ?? null);
  const [deleting, setDeleting] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const { preference, setPreference } = useTheme();
  const [open, setOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowLanguages(false);
      }
    };
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const name = identity?.displayName ?? '';
  const initial = name.trim().charAt(0);
  // The strongest role held, which is what someone wants to see about
  // themselves — not the one they happen to be looking at.
  const roleKey = isAdmin ? 'admin' : isMuqri ? 'muqri' : 'student';

  const item = 'flex h-11 w-full items-center gap-3 rounded-[14px] px-3 text-sm transition-colors';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        data-testid="user-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('user.menu')}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          'flex w-full items-center gap-3 rounded-[16px] p-1.5 text-start transition-colors hover:bg-surface',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          collapsed && 'lg:justify-center',
        )}
      >
        <span
          data-testid="user-avatar"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tint text-sm font-semibold text-primary"
        >
          {initial || <UserIcon className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden="true" />}
        </span>
        <span className={cn('min-w-0 flex-1', collapsed && 'lg:hidden')}>
          <span className="block truncate text-[15px] font-semibold text-ink">{name}</span>
          <span className="block truncate text-sm text-helper">{t(`roles.${roleKey}`)}</span>
        </span>
        <ChevronsUpDown
          className={cn('h-4 w-4 shrink-0 text-helper', collapsed && 'lg:hidden')}
          aria-hidden="true"
        />
      </button>

      {open && (
        // Opens UPWARD: the trigger sits at the foot of the sidebar.
        <div className="card-soft absolute bottom-full start-0 z-50 mb-2 w-72 overflow-hidden p-2">
          <div className="flex items-center gap-3 px-2 pb-3 pt-1">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tint text-primary">
              {initial || <UserIcon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />}
            </span>
            <div className="min-w-0">
              <p data-testid="user-menu-name" className="truncate font-semibold text-ink">
                {name}
              </p>
              <p data-testid="user-menu-role" className="truncate text-sm text-helper">
                {t(`roles.${roleKey}`)}
              </p>
            </div>
          </div>

          {/*
            * A LINK, not a button that navigates: it is a destination, so it
            * opens in a new tab on a middle-click and shows its URL on hover
            * like every other route in the app.
            */}
          <Link
            to="/profile"
            data-testid="user-menu-profile"
            onClick={() => setOpen(false)}
            className={cn(item, 'text-ink hover:bg-surface')}
          >
            <Pencil className="h-4 w-4 shrink-0" strokeWidth={1.8} />
            <span className="flex-1 text-start">{t('user.editProfile')}</span>
          </Link>

          {/* فاتح · داكن · تلقائي, saved per browser — the phone menu's choice. */}
          <div className="px-3 pb-2 pt-3">
            <p className="mb-2 text-xs font-medium text-helper">{t('menu.appearance')}</p>
            <div
              role="radiogroup"
              aria-label={t('menu.appearance')}
              data-testid="menu-theme"
              className="flex rounded-full bg-surface p-1"
            >
              {THEMES.map(({ value, labelKey, icon: Icon }) => {
                const chosen = preference === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={chosen}
                    data-testid={`theme-${value}`}
                    onClick={() => setPreference(value)}
                    className={cn(
                      'flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full text-sm transition-colors',
                      chosen
                        ? 'bg-primary font-semibold text-primary-foreground'
                        : 'text-helper hover:text-ink',
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            data-testid="user-menu-language"
            onClick={() => setShowLanguages((v) => !v)}
            aria-expanded={showLanguages}
            className={cn(item, 'text-ink hover:bg-surface')}
          >
            <Globe className="h-4 w-4 shrink-0" strokeWidth={1.8} />
            <span className="flex-1 text-start">{t('user.language')}</span>
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showLanguages && 'rotate-180')} />
          </button>

          {showLanguages && (
            <div role="radiogroup" aria-label={t('user.language')}>
              {LANGUAGES.map(({ code, nativeName }) => (
                <button
                  key={code}
                  type="button"
                  role="radio"
                  aria-checked={language === code}
                  data-testid={`language-option-${code}`}
                  onClick={() => { setLanguage(code); setShowLanguages(false); setOpen(false); }}
                  className={cn(
                    item, 'justify-between hover:bg-surface',
                    language === code ? 'font-semibold text-primary' : 'text-ink',
                  )}
                >
                  <span className="ps-7">{nativeName}</span>
                  {language === code && <Check className="h-3.5 w-3.5" />}
                </button>
              ))}
            </div>
          )}

          {/* Handing the device to a child, or taking it back. */}
          <ProfileSwitcher item={item} onDone={() => setOpen(false)} />

          <div className="my-1.5 border-t border-hair" />

          <button
            type="button"
            data-testid="user-menu-signout"
            onClick={() => {
              setOpen(false);
              // The shared helper closes today's session BEFORE dropping the
              // credential that closeSession needs.
              void signOutAndCloseSession({
                muqriId: identity?.userId ?? null,
                isMuqri,
                closeSession,
                signOut,
              });
            }}
            className={cn(item, 'text-danger hover:bg-danger/10')}
          >
            <LogOut className="h-4 w-4 shrink-0" strokeWidth={1.8} />
            {t('auth.signOut')}
          </button>

          {!managedBy && (
            <button
              type="button"
              data-testid="user-menu-delete-account"
              disabled={deleting}
              onClick={async () => {
                // eslint-disable-next-line no-alert -- the browser's own
                // confirm is the one dialog a person already trusts for this.
                if (!window.confirm(`${t('auth.deleteAccount')}

${t('auth.deleteAccountWhy')}`)) return;
                setDeleting(true);
                try {
                  await deleteMyAccount();
                  await signOut();
                } catch {
                  setDeleting(false);
                  // eslint-disable-next-line no-alert
                  window.alert(t('auth.deleteAccountFailed'));
                }
              }}
              className={cn(item, 'text-danger hover:bg-danger/10 disabled:opacity-60')}
            >
              <Trash2 className="h-4 w-4 shrink-0" strokeWidth={1.8} />
              {t('auth.deleteAccount')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
